import { apiRequest, queryClient } from "@/lib/queryClient";

export type PendingCannedUse = { id: number; insertedText: string };

/**
 * Task #2120 — Record canned-response usage only when the message containing
 * the insertion is actually sent. We accept the list of insertions tagged by
 * the picker (or inline trigger) and the final sent body, and for each unique
 * canned response whose inserted text still appears in the sent body we fire
 * the legacy `record-use` endpoint. This replaces the picker-time fire-and-
 * forget call, which was incrementing the use counter even when the host
 * immediately deleted the inserted text or never sent the message.
 */
export function recordCannedUsesIfPresent(
  pendingUses: PendingCannedUse[],
  sentBody: string,
): void {
  if (pendingUses.length === 0) return;
  const body = (sentBody ?? "").trim();
  if (!body) return;
  const seen = new Set<number>();
  const toRecord: number[] = [];
  for (const u of pendingUses) {
    if (seen.has(u.id)) continue;
    const needle = (u.insertedText ?? "").trim();
    if (!needle) continue;
    if (body.includes(needle)) {
      seen.add(u.id);
      toRecord.push(u.id);
    }
  }
  if (toRecord.length === 0) return;
  Promise.all(
    toRecord.map((id) =>
      apiRequest("POST", `/api/app/canned-responses/${id}/record-use`).catch(
        () => undefined,
      ),
    ),
  ).then(() => {
    queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses"] });
  });
}
